import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    IconButton,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    InputAdornment
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CompactVoiceRecorder from '../Contacts/CompactVoiceRecorder';

const EditInteractionDialog = ({
    open,
    onClose,
    interaction,
    onSave
}) => {
    const [editedInteraction, setEditedInteraction] = useState({
        date: dayjs(),
        notes: '',
        ratings: {
            commitmentAndIntensity: '',
            initiativeAndReciprocity: '',
            emotionalInvolvement: '',
            opennessAndTrust: ''
        }
    });

    // Update state when interaction prop changes
    useEffect(() => {
        if (interaction) {
            setEditedInteraction({
                date: interaction.date ? dayjs(interaction.date) : dayjs(),
                notes: interaction.notes || '',
                ratings: {
                    commitmentAndIntensity: interaction.ratings?.commitmentAndIntensity || '',
                    initiativeAndReciprocity: interaction.ratings?.initiativeAndReciprocity || '',
                    emotionalInvolvement: interaction.ratings?.emotionalInvolvement || '',
                    opennessAndTrust: interaction.ratings?.opennessAndTrust || ''
                }
            });
        }
    }, [interaction]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedInteraction(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRatingChange = (ratingKey, value) => {
        setEditedInteraction(prev => ({
            ...prev,
            ratings: {
                ...prev.ratings,
                [ratingKey]: value
            }
        }));
    };

    const handleDateChange = (newDate) => {
        setEditedInteraction(prev => ({
            ...prev,
            date: newDate
        }));
    };

    const handleTranscriptionComplete = (transcribedText) => {
        setEditedInteraction(prev => ({
            ...prev,
            notes: prev.notes ? `${prev.notes}\n\n${transcribedText}` : transcribedText
        }));
    };

    const handleSubmit = () => {
        const data = {
            ...editedInteraction,
            date: editedInteraction.date.toISOString()
        };

        // Only include ratings if at least one rating is provided
        const hasRatings = Object.values(editedInteraction.ratings).some(rating => rating !== '');
        if (hasRatings) {
            // Convert empty strings to null for ratings
            data.ratings = {};
            Object.keys(editedInteraction.ratings).forEach(key => {
                data.ratings[key] = editedInteraction.ratings[key] === '' ? null : parseInt(editedInteraction.ratings[key]);
            });
        } else {
            // If no ratings provided, send null to clear any existing ratings
            data.ratings = {
                commitmentAndIntensity: null,
                initiativeAndReciprocity: null,
                emotionalInvolvement: null,
                opennessAndTrust: null
            };
        }

        onSave(data);
    };

    if (!interaction) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Edit touch
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Date & Time"
                            value={editedInteraction.date}
                            onChange={handleDateChange}
                            sx={{ width: '100%', mb: 2 }}
                        />
                    </LocalizationProvider>

                    <TextField
                        name="notes"
                        label="Notes"
                        multiline
                        rows={4}
                        value={editedInteraction.notes}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        placeholder="What happened? What should you remember next time?"
                        helperText="Keep the memory simple: what happened, what mattered, and what to remember next."
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.9375rem',
                                lineHeight: 1.6
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ alignSelf: 'center', mr: -0.5 }}>
                                    <CompactVoiceRecorder 
                                        onTranscriptionComplete={handleTranscriptionComplete}
                                        onError={(err) => console.error('Voice recording error:', err)}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Optional relationship signal */}
                    <Box sx={{ mt: 2 }}>
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="ratings-content"
                                id="ratings-header"
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RateReviewIcon color="action" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 650 }}>
                                        Relationship signal
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Optional notes for your own memory. Use them gently, not as a scorecard.
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {/* Commitment and Intensity */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Commitment and Intensity
                                        </FormLabel>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                            How present did this person feel?
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={editedInteraction.ratings.commitmentAndIntensity}
                                            onChange={(e) => handleRatingChange('commitmentAndIntensity', e.target.value)}
                                        >
                                            <FormControlLabel value="" control={<Radio />} label="Not set" />
                                            <FormControlLabel value="1" control={<Radio />} label="Quiet" />
                                            <FormControlLabel value="2" control={<Radio />} label="Steady" />
                                            <FormControlLabel value="3" control={<Radio />} label="Strong" />
                                        </RadioGroup>
                                    </FormControl>

                                    <Divider />

                                    {/* Initiative and Reciprocity */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Initiative and Reciprocity
                                        </FormLabel>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                            Did the energy move both ways?
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={editedInteraction.ratings.initiativeAndReciprocity}
                                            onChange={(e) => handleRatingChange('initiativeAndReciprocity', e.target.value)}
                                        >
                                            <FormControlLabel value="" control={<Radio />} label="Not set" />
                                            <FormControlLabel value="1" control={<Radio />} label="Quiet" />
                                            <FormControlLabel value="2" control={<Radio />} label="Steady" />
                                            <FormControlLabel value="3" control={<Radio />} label="Strong" />
                                        </RadioGroup>
                                    </FormControl>

                                    <Divider />

                                    {/* Emotional Involvement */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Warmth
                                        </FormLabel>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                            Did the touch feel warm or personal?
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={editedInteraction.ratings.emotionalInvolvement}
                                            onChange={(e) => handleRatingChange('emotionalInvolvement', e.target.value)}
                                        >
                                            <FormControlLabel value="" control={<Radio />} label="Not set" />
                                            <FormControlLabel value="1" control={<Radio />} label="Quiet" />
                                            <FormControlLabel value="2" control={<Radio />} label="Steady" />
                                            <FormControlLabel value="3" control={<Radio />} label="Strong" />
                                        </RadioGroup>
                                    </FormControl>

                                    <Divider />

                                    {/* Openness and Trust */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Openness and Trust
                                        </FormLabel>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                            Was there openness or trust in the conversation?
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={editedInteraction.ratings.opennessAndTrust}
                                            onChange={(e) => handleRatingChange('opennessAndTrust', e.target.value)}
                                        >
                                            <FormControlLabel value="" control={<Radio />} label="Not set" />
                                            <FormControlLabel value="1" control={<Radio />} label="Quiet" />
                                            <FormControlLabel value="2" control={<Radio />} label="Steady" />
                                            <FormControlLabel value="3" control={<Radio />} label="Strong" />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditInteractionDialog;
